<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { normalizeCandidateReportCachePath } from "#bb/lib/pathContract";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string; defaultOpen?: boolean }>();

const { field } = useInlineBlockEditor(props.id);
const { getContentById, updateContentById } = useContentManager();
const route = useRoute();
const injectedReportUserId = inject<{ value?: string } | null>("reportUserId", null);

// The provider that owns the live data renders in a different subtree (the
// report preview canvas), so we can't reach it via provide/inject. Drive the
// refresh over the global event bus keyed by this block's id: emit a request,
// the provider reloads the live report file (the system-cache aggregate served
// by the report API, bypassing the client cache) and emits the result back here
// so we can surface progress + outcome.
type RefreshResult = { ok: boolean; users?: number; rows?: number; error?: string };
const refreshing = ref(false);
const refreshResult = ref<RefreshResult | null>(null);
const refreshRequestKey = `component:onRefreshLiveData:${props.id}`;
const refreshResultKey = `component:onLiveDataRefreshed:${props.id}`;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function onRefreshResult(result: RefreshResult) {
  clearRefreshTimer();
  refreshResult.value = result;
  refreshing.value = false;
}

const refreshLiveData = () => {
  if (refreshing.value) return;
  refreshing.value = true;
  refreshResult.value = null;
  (eventBus as any).emit(refreshRequestKey);
  clearRefreshTimer();
  refreshTimer = setTimeout(() => {
    if (refreshing.value) {
      refreshing.value = false;
      refreshResult.value = { ok: false, error: "timeout" };
    }
  }, 20000);
};

onMounted(() => (eventBus as any).on(refreshResultKey, onRefreshResult));
onUnmounted(() => {
  (eventBus as any).off(refreshResultKey, onRefreshResult);
  clearRefreshTimer();
});

const defaultMapper = `function mapper(data){
  return data.map((c) => ({
    name: String(c.name ?? ""),
    score: Number(c.score ?? 0),
  }));
}`;

const reportFileName = field<string>("reportFileName", "report");
const editorCandidateId = computed(() =>
  injectedReportUserId?.value
  ?? (typeof route.query.user === "string" ? route.query.user : null),
);
const reportCachePath = computed({
  get: () => normalizeCandidateReportCachePath(reportFileName.value),
  set: (value: string) => {
    reportFileName.value = value;
  },
});
const customMapperOpen = ref(false);
const sampleDataOpen = ref(false);
const isFullscreen = ref(false);
const draftCustomMapper = ref(defaultMapper);
const draftSampleData = ref("");

const monacoOptions = ref({
  automaticLayout: true,
  wordWrap: "on",
  minimap: { enabled: false },
});

const drawerWidthClass = computed(() =>
  isFullscreen.value
    ? "data-[vaul-drawer-direction=right]:w-screen data-[vaul-drawer-direction=right]:sm:max-w-none"
    : "data-[vaul-drawer-direction=right]:w-[65vw] data-[vaul-drawer-direction=right]:sm:max-w-[1080px]"
);

const openCustomMapper = () => {
  const current = getContentById(props.id)?.customMapper;
  draftCustomMapper.value = typeof current === "string" && current.trim() ? current : defaultMapper;
  isFullscreen.value = false;
  customMapperOpen.value = true;
};

const openSampleData = () => {
  const current = getContentById(props.id)?.sampleData;
  draftSampleData.value = typeof current === "string" ? current : "";
  isFullscreen.value = false;
  sampleDataOpen.value = true;
};

const saveCustomMapper = () => {
  updateContentById(props.id, { customMapper: draftCustomMapper.value });
  customMapperOpen.value = false;
};

const saveSampleData = () => {
  updateContentById(props.id, { sampleData: draftSampleData.value });
  sampleDataOpen.value = false;
};
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:box-select" accent="reporting" :default-open="defaultOpen ?? true">
    <EditorField :label="t('reportFileNameLabel')" :hint="t('reportFileNameHint')">
      <EditorPathInput
        v-model="reportCachePath"
        mode="report-cache"
        :scopes="['user']"
        default-scope="user"
        :user-id="editorCandidateId"
        placeholder="/data/<userId>/cache/report"
        aria-label="Report cache path"
      />
    </EditorField>

    <div class="space-y-1">
      <Button
        variant="outline"
        size="sm"
        class="w-full justify-start rounded-sm"
        :disabled="refreshing"
        @click="refreshLiveData"
      >
        <Icon
          mode="svg"
          size="15"
          :name="refreshing ? 'lucide:loader-circle' : 'lucide:refresh-cw'"
          :class="['mr-2 text-muted-foreground', refreshing && 'animate-spin']"
        />
        {{ refreshing ? t("refreshingLabel") : t("refreshLabel") }}
      </Button>
      <p class="px-0.5 text-[11px] leading-snug text-muted-foreground">{{ t("refreshHint") }}</p>
      <p
        v-if="refreshResult && refreshResult.ok"
        class="px-0.5 text-[11px] font-medium leading-snug text-emerald-600"
      >
        {{ t("refreshLoaded", { count: refreshResult.users ?? 0 }) }}
      </p>
      <p
        v-else-if="refreshResult && !refreshResult.ok"
        class="px-0.5 text-[11px] font-medium leading-snug text-destructive"
      >
        {{ refreshResult.error === "timeout" ? t("refreshTimeout") : t("refreshFailed") }}
      </p>
    </div>

    <div class="space-y-2">
      <Button variant="outline" size="sm" class="w-full justify-start rounded-sm" @click="openCustomMapper">
        <Icon mode="svg" size="15" name="lucide:file-code-2" class="mr-2 text-muted-foreground" />
        {{ t("customMapperLabel") }}
      </Button>
      <Button variant="outline" size="sm" class="w-full justify-start rounded-sm" @click="openSampleData">
        <Icon mode="svg" size="15" name="lucide:braces" class="mr-2 text-muted-foreground" />
        {{ t("sampleDataLabel") }}
      </Button>
    </div>

    <Drawer v-model:open="customMapperOpen" direction="right">
      <DrawerContent :class="['flex h-full flex-col transition-[width] duration-200', drawerWidthClass]">
        <DrawerHeader class="flex flex-row items-start justify-between">
          <div>
            <DrawerTitle>{{ t("customMapperTitle") }}</DrawerTitle>
            <DrawerDescription>{{ t("customMapperDescription") }}</DrawerDescription>
          </div>
          <Button variant="ghost" size="icon" class="size-8 shrink-0" @click="isFullscreen = !isFullscreen">
            <Icon mode="svg" size="16" :name="isFullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'" />
          </Button>
        </DrawerHeader>
        <div class="flex-1 overflow-hidden px-4">
          <ClientOnly>
            <MonacoEditor
              v-model="draftCustomMapper"
              lang="javascript"
              :options="monacoOptions"
              class="h-full w-full rounded-sm border"
            />
          </ClientOnly>
        </div>
        <DrawerFooter>
          <Button @click="saveCustomMapper">{{ t("save") }}</Button>
          <DrawerClose as-child>
            <Button variant="outline">{{ t("cancel") }}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Drawer v-model:open="sampleDataOpen" direction="right">
      <DrawerContent :class="['flex h-full flex-col transition-[width] duration-200', drawerWidthClass]">
        <DrawerHeader class="flex flex-row items-start justify-between">
          <div>
            <DrawerTitle>{{ t("sampleDataTitle") }}</DrawerTitle>
            <DrawerDescription>{{ t("sampleDataDescription") }}</DrawerDescription>
          </div>
          <Button variant="ghost" size="icon" class="size-8 shrink-0" @click="isFullscreen = !isFullscreen">
            <Icon mode="svg" size="16" :name="isFullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'" />
          </Button>
        </DrawerHeader>
        <div class="flex-1 overflow-hidden px-4">
          <ClientOnly>
            <MonacoEditor
              v-model="draftSampleData"
              lang="json"
              :options="monacoOptions"
              class="h-full w-full rounded-sm border"
            />
          </ClientOnly>
        </div>
        <DrawerFooter>
          <Button @click="saveSampleData">{{ t("save") }}</Button>
          <DrawerClose as-child>
            <Button variant="outline">{{ t("cancel") }}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Report Provider"
  reportFileNameLabel: "Report Cache Path"
  reportFileNameHint: "Selects the candidate cache path. The report API receives its filename while legacy filename-only values remain supported."
  refreshLabel: "Load live data"
  refreshingLabel: "Loading live data…"
  refreshHint: "Fetches the full report file (all candidates) so mapping + fields use real data, not the sample."
  refreshLoaded: "Loaded {count} candidates from the live report file."
  refreshFailed: "Couldn't load live data. Check the report file name and try again."
  refreshTimeout: "No response from the report preview. Expand the provider block, then try again."
  customMapperLabel: "Custom Mapper"
  sampleDataLabel: "Sample Data (raw report JSON)"
  customMapperTitle: "Edit Custom Mapper"
  customMapperDescription: "Update the JavaScript mapper used to shape report data."
  sampleDataTitle: "Edit Sample Data"
  sampleDataDescription: "Update the raw report JSON used while editing this report provider."
  save: "Save"
  cancel: "Cancel"
</i18n>
